import React, {
    ReactNode,
    createContext,
    useContext,
    useMemo,
    useState,
} from 'react';

export type TPersonalInformation = {
    FirstName: string;
    MiddleInitial: string;
    LastName: string;
    EmailAddress: string;
    /**
     * Address Field
     */
    MailingAddressLine1: string;
    /**
     * Apt/Unit Field
     */
    MailingAddressLine2: string;
    MailingCity: string;
    MailingState: string;
    MailingZipCode: string;
    PrimaryPhoneNumber: string;
    Ssn: string;
    dob: string;
};

interface IPersonalInformationPayload {
    personalInformation: Record<keyof TPersonalInformation, string> //TPersonalInformation;
    setSingleValue(
        name: keyof TPersonalInformation,
    ): (newValue: string) => void;
    batchSetValues(payload: Partial<TPersonalInformation>): void;
    setIsSubmit?: React.Dispatch<React.SetStateAction<any>>;
    isSubmit?: boolean;
}

const defaultPersonalInformation: TPersonalInformation = {
    FirstName: '',
    MiddleInitial: '',
    LastName: '',
    EmailAddress: '',
    MailingAddressLine1: '',
    MailingAddressLine2: '',
    MailingCity: '',
    MailingState: '',
    MailingZipCode: '',
    PrimaryPhoneNumber: '',
    Ssn: '',
    dob: '',
};

const PersonalInformationContext = createContext<IPersonalInformationPayload>({
    personalInformation: defaultPersonalInformation,
    setSingleValue: () => () => {},
    batchSetValues: () => {},
    isSubmit: false,
    setIsSubmit: () => {},
});

export default function PersonalInformationProvider({
    children,
}: {
    children: ReactNode;
}) {
    const [personalInformation, setPersonalInformation] =
        useState<TPersonalInformation>(defaultPersonalInformation);
    
    const [isSubmit, setIsSubmit] = useState<boolean>(false);

    const setSingleValue =
        (name: keyof TPersonalInformation) => (newValue: string) => {
            setPersonalInformation(current => {
                return {
                    ...current,
                    [name]: newValue,
                };
            });
        };

    const batchSetValues = (
        payload: Partial<TPersonalInformation>,
    ) => {
        setPersonalInformation(current => {
            return {
                ...current,
                ...payload,
            };
        });
    };

    return (
        <PersonalInformationContext.Provider
            value={useMemo(
                () => ({
                    personalInformation,
                    setSingleValue,
                    batchSetValues,
                    isSubmit,
                    setIsSubmit,
                }),
                [personalInformation],
            )}
        >
            {children}
        </PersonalInformationContext.Provider>
    );
}

export const usePersonalInformation = () =>
    useContext(PersonalInformationContext);
